import React, { useMemo, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { RequestState } from '../../../../dorian-shared/hooks/useAsyncOperationState';
import { showToast } from '../../../ui/utils';
import { PageWrapper } from '../../../ui/PageWrapper';
import { ConfirmModal } from '../../../ui/ConfirmModal/ConfirmModal';
import { OfferForm } from '../components/OfferForm/OfferForm';
import { OfferFormValues, offerToFormValues } from '../components/OfferForm/offerFormLogic';
import { useUpdateOffer } from '../useUpdateOffer';
import { useUnsavedChangesGuard } from '../useUnsavedChangesGuard';
import { useOffer } from './useOffer';

function getPageLayout(title: string) {
  return {
    header: { title, settings: 'admin' },
    sidebar: {
      nav: [
        {
          title: 'Back',
          href: '/home/',
          action: window.history.length > 1 && document.referrer !== window.location.href
            ? () => { window.history.back(); } : null,
          variant: 'secondary',
        },
        { title: 'Offers', href: '/offers' },
      ],
    },
  };
}

export function OfferItemPage(props: RouteComponentProps<{ id: string }>) {
  const { match, history } = props;
  const offerId = Number(match.params.id);
  const {
    requestState, offer, setOffer, notFound,
  } = useOffer(offerId);
  const { isLoading, updateOffer } = useUpdateOffer(offerId);
  const [submitError, setSubmitError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const {
    isConfirmVisible, confirmLeave, cancelLeave,
  } = useUnsavedChangesGuard(hasUnsavedChanges);

  const pageLayout = getPageLayout(offer?.display_name ?? 'Loading...');
  const formValues = useMemo(() => (offer ? offerToFormValues(offer) : undefined), [offer]);

  const handleSubmit = (values: OfferFormValues) => {
    setSubmitError('');
    updateOffer(values)
      .then((updated) => {
        setOffer(updated);
        showToast({ textMessage: `Offer "${updated.display_name}" saved.`, variant: 'success' });
      })
      .catch((error) => {
        setSubmitError(error.message);
        // An error must stay legible long enough to read, not just distinguishable by colour --
        // matches the AchievementsFormikWrapper save-error precedent (7s vs the 4s default).
        showToast({ textMessage: error.message, variant: 'danger', timeout: 7000 });
      });
  };

  return (
    <PageWrapper {...props} page={pageLayout}>
      <Card>
        <Card.Body>
          {requestState === RequestState.Loading && (
            <div className="text-center">
              <Spinner variant="primary" animation="border" className="loadingSpinner justify-content-center" />
            </div>
          )}
          {requestState === RequestState.Error && notFound && (
            <div className="text-center">
              <h5>Offer not found.</h5>
              <p>
                This offer may have been deleted.
                {' '}
                <Link to="/offers">Go back to the offers list.</Link>
              </p>
            </div>
          )}
          {requestState === RequestState.Error && !notFound && (
            <div className="text-center">
              <h5>This offer couldn’t be loaded.</h5>
              <p>The service may be temporarily unavailable. Refresh the page to try again.</p>
            </div>
          )}
          {requestState === RequestState.Success && offer && formValues && (
            <OfferForm
              offerFormValues={formValues}
              onSubmit={handleSubmit}
              onCancel={() => history.push('/offers')}
              isLoading={isLoading}
              isEditing
              submitError={submitError}
              onDirtyChange={setHasUnsavedChanges}
            />
          )}
        </Card.Body>
      </Card>
      <ConfirmModal
        centered
        show={isConfirmVisible}
        isLoading={false}
        title="Unsaved changes"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
        confirmButtonTitle="Leave"
        cancelButtonTitle="Stay"
        backdrop="static"
      >
        You have unsaved changes. Leave without saving?
      </ConfirmModal>
    </PageWrapper>
  );
}
